import * as React from 'react';
import classnames from '../utils/classnames.tsx';
import DropdownMenu from './DropdownMenu.tsx';
import { withIcon } from './Icon.tsx';
import { faChevronDown, faMagnifyingGlass, faXmark } from '@fortawesome/pro-regular-svg-icons';
import Button from './Button.tsx';
import StringField from './Fields/StringField.tsx';
import { useTranslation } from 'react-i18next';
import { useControllableState } from '../utils/useControllableState.tsx';
import { useEffect, useRef, useState } from 'react';
import { groupBy } from 'lodash';
import useStickyState from '../utils/useStickyState.tsx';

export interface FilterDropdownOption {
    label: string;
    value: string;
    groupName?: string;
    // renders a divider directly above this option, e.g. to set a sentinel value (like "No agent
    // connected") apart from the real options above it
    separatorBefore?: boolean;
}

interface FilterDropdownProps {
    allLabel?: string;
    label?: string;

    options: FilterDropdownOption[];
    renderOption?: (option: FilterDropdownOption) => React.ReactNode; // TODO maybe this is not the cleanest way to do this
    filterOptions?: (options: FilterDropdownOption[], searchValue: string) => FilterDropdownOption[];

    renderGroupName?: (groupName: string) => React.ReactNode;

    active?: boolean;
    selected?: string[];
    onSelectedChange?: (selected: string[]) => void;
    multiple?: boolean;
    showMultipleCount?: boolean;

    maxOptions?: number;

    searchValue?: string;
    onSearchValueChange?: (searchValue: string) => void;

    showSearch?: boolean;
    required?: boolean;
    className?: string;
    dropdownClassname?: string;

    extraContent?: React.ReactNode;
    formatLabel?;
    icon?: React.ReactNode; // Optional icon to prepend
    variant?: 'outline' | 'ghost';
}

const ChevronDownIcon = withIcon(faChevronDown);
const ClearIcon = withIcon(faXmark);
const SearchIcon = withIcon(faMagnifyingGlass);

const defaultFilterOptions = (options: FilterDropdownOption[], searchValue: string) => {
    return options.filter(
        (option) =>
            searchValue === '' ||
            option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
            option.value.toLowerCase().includes(searchValue.toLowerCase())
    );
};

export const defaultFormatLabel = ({ defaultLabel, allLabel, options, selected, searchValue, multiple }) => {
    // in case no item is selected we show the default label
    let label: string = defaultLabel;
    if (!multiple || options.length === 1 || (multiple && selected.length === 1)) {
        // in case only one option is allowed or only one option given we either show that option or the default label
        // OR in case only one option is selected we show that option
        const selectedOption = options.find((option) => option.value === selected[0]);
        label = selectedOption?.label ?? selected[0] ?? defaultLabel;
        if (label !== defaultLabel) {
            label = `${defaultLabel}: ${label}`;
        }
    } else if (selected.length === options.length && !searchValue) {
        // in case all options are selected we show the all label
        label = allLabel;
    }
    return label;
};

export const TriggerButtonIndicator = ({
    className,
    active,
    ...props
}: React.ComponentPropsWithoutRef<'span'> & { active?: boolean }) => {
    return (
        <span
            className={classnames(
                'rounded-full cursor-pointer hover:bg-surface-brand-strong/20 active:bg-surface-brand-strong/30 transition-colors flex items-center justify-center w-5.5 h-5.5 -mx-1.5 -my-0.5 text-content-strong',
                active && 'text-content-brand-strong',
                className
            )}
            {...props}
        />
    );
};

export const StickyFirstItem = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
    return (
        <div
            className={classnames(
                'flex flex-col gap-1 sticky top-0 bg-surface-weak border-b border-line-weak pb-1 mb-1 z-20',
                className
            )}
            {...props}
        />
    );
};

const FilterDropdown = ({
    allLabel,
    label: defaultLabel,
    formatLabel = defaultFormatLabel,

    options,
    renderOption,
    filterOptions = defaultFilterOptions,

    active = false,
    renderGroupName,

    selected = [],
    onSelectedChange,
    multiple = false,
    maxOptions = 20,

    searchValue: propsSearchValue,
    onSearchValueChange,

    showSearch = false,
    required = false,
    showMultipleCount = true,
    className,
    dropdownClassname,
    extraContent,
    icon,
    variant = 'outline',
}: FilterDropdownProps) => {
    const { t } = useTranslation('assistance');
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useControllableState('', propsSearchValue, onSearchValueChange);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const filteredOptions = options ? filterOptions(options, searchValue) : [];

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const label = formatLabel({ defaultLabel, allLabel, options, selected, searchValue, multiple });

    const handleCheckedChange = (value: string) => (checked: boolean) => {
        if (checked) {
            onSelectedChange?.(multiple ? [...selected, value] : [value]);
        } else {
            onSelectedChange?.(multiple ? selected.filter((selectedValue) => selectedValue !== value) : []);
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onSelectedChange?.([]);
    };

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
    };

    const filteredOptionGroups = groupBy(filteredOptions, (option) => option.groupName || '');
    const isActive = active || isOpen || selected.length > 0;

    return (
        <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
            <DropdownMenu.Trigger
                onPointerDown={(e) => e.preventDefault()}
                onClick={() => handleOpenChange(!isOpen)}
                asChild
            >
                <Button
                    variant={variant}
                    className={classnames('flex flex-row shrink-0 gap-2 items-center whitespace-nowrap', className)}
                    active={isActive}
                >
                    {icon && <span>{icon}</span>}

                    <span>{label}</span>

                    {multiple && showMultipleCount && selected.length > 1 && selected.length != options.length ? (
                        <span>({selected.length})</span>
                    ) : null}

                    {!required && selected.length > 0 ? (
                        <TriggerButtonIndicator onClick={handleClear} active={isActive}>
                            <ClearIcon />
                        </TriggerButtonIndicator>
                    ) : (
                        <TriggerButtonIndicator className="pointer-events-none" active={isActive}>
                            <ChevronDownIcon />
                        </TriggerButtonIndicator>
                    )}
                </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="start"
                    className={classnames('!max-h-[20rem] overflow-y-auto !min-w-auto', dropdownClassname)}
                >
                    <div className="overflow-y-auto">
                        <StickyFirstItem className={classnames(!showSearch && !multiple && 'hidden')}>
                            {showSearch && (
                                <div className="relative">
                                    <SearchIcon className="absolute pointer-events-none top-1/2 left-3.5 transform -translate-y-1/2 z-10 text-sm text-content-weak" />
                                    <StringField
                                        value={searchValue}
                                        onValueChange={setSearchValue}
                                        placeholder={t('overview.filters.search.placeholder')}
                                        inputRef={searchInputRef}
                                        className="hover:border-line-brand-default focus-within:border-line-brand-default focus-within:outline-surface-brand pl-5"
                                        onFocus={() => setIsSearchFocused(true)}
                                        onBlur={() => setIsSearchFocused(false)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                </div>
                            )}
                            {multiple && filteredOptions.length > 0 && (
                                <DropdownMenu.CheckboxItem
                                    className="outline-none"
                                    onSelect={(e) => e.preventDefault()}
                                    checked={
                                        selected.length === options.length
                                            ? true
                                            : selected.length === 0
                                              ? false
                                              : 'indeterminate'
                                    }
                                    onCheckedChange={() => {
                                        if (selected.length === options.length) {
                                            onSelectedChange?.([]);
                                        } else {
                                            onSelectedChange?.(filteredOptions.map((option) => option.value));
                                        }
                                    }}
                                >
                                    {selected.length === options.length
                                        ? t('overview.filters.selectNone')
                                        : t('overview.filters.selectAll')}
                                </DropdownMenu.CheckboxItem>
                            )}
                        </StickyFirstItem>

                        {Object.entries(filteredOptionGroups).length > 0 ? (
                            Object.entries(filteredOptionGroups).map(([groupName, groupOptions]) => {
                                return (
                                    <React.Fragment key={groupName}>
                                        {groupName && (
                                            <DropdownMenu.Label className="">
                                                {renderGroupName ? renderGroupName(groupName) : groupName}
                                            </DropdownMenu.Label>
                                        )}
                                        {multiple ? (
                                            groupOptions?.map((option) => (
                                                <React.Fragment key={option.value}>
                                                    {option.separatorBefore && <DropdownMenu.Separator />}
                                                    <DropdownMenu.CheckboxItem
                                                        onSelect={(e) => e.preventDefault()}
                                                        onCheckedChange={handleCheckedChange(option.value)}
                                                        checked={selected.includes(option.value)}
                                                        onPointerLeave={(event) =>
                                                            isSearchFocused && event.preventDefault()
                                                        }
                                                        onPointerMove={(event) =>
                                                            isSearchFocused && event.preventDefault()
                                                        }
                                                        className="outline-none"
                                                    >
                                                        {renderOption ? renderOption(option) : option.label}
                                                    </DropdownMenu.CheckboxItem>
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <DropdownMenu.RadioGroup
                                                value={selected[0]}
                                                onValueChange={(value) => onSelectedChange?.([value])}
                                            >
                                                {groupOptions?.map((option) => (
                                                    <React.Fragment key={option.value}>
                                                        {option.separatorBefore && <DropdownMenu.Separator />}
                                                        <DropdownMenu.RadioItem
                                                            value={option.value}
                                                            className="outline-none"
                                                            onPointerLeave={(event) =>
                                                                isSearchFocused && event.preventDefault()
                                                            }
                                                            onPointerMove={(event) =>
                                                                isSearchFocused && event.preventDefault()
                                                            }
                                                        >
                                                            {renderOption ? renderOption(option) : option.label}
                                                        </DropdownMenu.RadioItem>
                                                    </React.Fragment>
                                                ))}
                                            </DropdownMenu.RadioGroup>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <div className="p-2 text-content-weak text-sm">{t('overview.filters.noResults')}</div>
                        )}
                    </div>
                    {extraContent}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu>
    );
};

export default FilterDropdown;

const FilterGroupButton = ({ active, className, ...props }: React.ComponentPropsWithRef<typeof Button>) => {
    return (
        <Button
            className={classnames(
                'rounded-none -mr-px last:mr-0 first:rounded-l last:rounded-r hover:z-10 data-[state=open]:z-10 shadow-none',
                active
                    ? 'z-20'
                    : 'border-l-transparent border-r-transparent first:border-l-line-strong last:border-r-line-strong',
                className
            )}
            active={active}
            {...props}
        />
    );
};

const FilterGroupRoot = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return <div className={classnames('flex shadow-control rounded', className)} {...props} />;
};

export const FilterGroup = Object.assign(FilterGroupRoot, {
    Button: FilterGroupButton,
});
